import { useRef, useState } from "react";
import { toast } from "sonner";
import { extractError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, Plus } from "lucide-react";

function resolveUrl(u) {
  if (!u) return null;
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/uploads/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

export default function MultiImageUpload({ value = [], onChange, testId = "multi" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const token = localStorage.getItem("mbk_token");
    const uploaded = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} > 5 Mo — ignoré`); continue; }
      if (!file.type.startsWith("image/")) continue;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API_BASE}/uploads`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
        if (!res.ok) throw new Error("upload failed");
        const data = await res.json();
        uploaded.push(data.url);
      } catch (err) {
        toast.error(extractError(err, `Erreur : ${file.name}`));
      }
    }
    if (uploaded.length) {
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} image(s) téléchargée(s)`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (i) => onChange((value || []).filter((_, idx) => idx !== i));

  return (
    <div>
      <input type="file" accept="image/*" multiple ref={inputRef} onChange={handleFiles} className="hidden" data-testid={`${testId}-input`} />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {(value || []).map((u, i) => (
          <div key={i} className="relative rounded-xl overflow-hidden border border-gray-200 aspect-square bg-gray-50">
            <img src={resolveUrl(u)} alt={`photo ${i + 1}`} className="w-full h-full object-cover" />
            <button type="button" onClick={() => removeAt(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 text-white flex items-center justify-center opacity-90 hover:opacity-100" data-testid={`${testId}-remove-${i}`}>
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-xl aspect-square border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 hover:border-[#0a1e3f] hover:bg-gray-50 text-gray-500" data-testid={`${testId}-add`}>
          {uploading ? <div className="text-xs">…</div> : (<><Plus className="h-5 w-5" /><span className="text-xs">Ajouter</span></>)}
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-2">PNG, JPG · max 5 Mo par image · sélection multiple possible</p>
    </div>
  );
}
